import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { DateTime } from 'luxon';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useMaxZ } from 'ui-components/hooks/services/use-max-z';
import { ConfirmCloseModal } from './components/confirm-close-modal/confirm-close-modal';
import { GuidService } from 'ui-components/services/guid-service';
import { Segment } from '../../../../services/analytics-services/segment';
import { LtlFreightTrackingEvents } from '../../tracking-events';
import type { LtlFreightBookingSource } from '../../tracking-events';
import { useTrackFreightBookingWizardChange } from './use-track-freight-booking-wizard-change';

import { jobGuidManagerFactory } from '@shipstation/async-client-manager';
import {
  FreightBookingWizard,
  getFirstInvalidStep,
  getStepsBeforeFirstInvalid,
  isReadyToBook,
  getIsReadyToQuote,
  WIZARD_MODE,
  WIZARD_STEP_IDS,
} from '@shipstation/client-common/components/freight-booking-wizard';
import { FREIGHT_FFP_STATUS_QUOTED, mapFreightFfpStatusId } from '@shipstation/client-common/ltl/freight-ffp-status';
import { reconcileSelection } from '@shipstation/client-common/components/freight-rate-card';
import {
  bookFulfillmentPlan,
  getAvailableFreightCustomAttributes,
  getBookedShipment,
  getCustomerLtlDefaults,
  saveConfiguration,
  saveCustomerLtlDefaults,
  saveQuoteToFulfillmentPlan,
  saveShipFromLtlDefaults,
} from '@shipstation/client-common/api/ltl.api';
import { createCustomer } from '@shipstation/client-common/api/customers.api';
import { saveShipToAndRecipient } from '@shipstation/client-common/api/fulfillment-plan.api';
import {
  buildBookFulfillmentPlanPayload,
  buildSaveConfigurationPayload,
  buildSaveQuoteToFulfillmentPlanPayload,
  createEmptyFormState,
  mapDimensionUnit,
  mapWeightUnit,
} from '@shipstation/client-common/ltl/freight-payload-mappers';
import { FREIGHT_INSUFFICIENT_INVENTORY_CODE } from '@shipstation/client-common/ltl/constants';
import { useFreightBookProgress } from '@shipstation/client-common/pusher/hooks/use-freight-book-progress';
import { useFreightFulfillmentPlanQuoteProgress } from '@shipstation/client-common/pusher/hooks/use-freight-fulfillment-plan-quote-progress';
import { useFreightQuoteProgress } from '@shipstation/client-common/pusher/hooks/use-freight-quote-progress';
import { useHandleBookingFormChange } from '@shipstation/client-common/ltl/hooks/use-handle-booking-form-change';
import {
  LTL_PUSHER_TIMEOUT_MS,
  LTL_BOOK_PUSHER_TIMEOUT_MS,
  makeLtlPusherService,
} from '@shipstation/client-common/pusher/ltl-fallback-helpers';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_DURATIONS } from 'ui-components/services/notification-service';
import { useUserHasConfigurationRole, useUserHasFreightBookRole } from '../../../../hooks/use-user-role';
import { freightActionButtonMessages } from '../../components/freight-action-buttons/messages';
import { editShipFrom } from '../../../../store/seller/seller.actions';
import {
  areCustomerLtlDefaultsDirty,
  areLtlDefaultsDirty,
  buildCustomerLtlDefaultsFromFormState,
  buildLtlDefaultsFromFormState,
  hasCompleteLtlPickupWindow,
  hydrateLtlDefaultsIntoFormState,
  hydrateGapFillLtlDefaultsIntoFormState,
  applyCustomerLtlDefaultsToFormState,
  hydrateCustomerLtlDefaultsIntoFormState,
} from '@shipstation/client-common/ltl/ltl-defaults';
import { useSubscribableState, freightQuoteState } from '../../../../subscribable-state';
import { useEnableLtlNotifications } from '../../../../feature-flags/hooks/use-enable-ltl-notifications';
import { useEnableFreightCustomAttributes } from '../../../../feature-flags/hooks/use-enable-freight-custom-attributes';
import { useEnableLtlFreight } from '../../../../feature-flags/hooks/use-enable-ltl-freight';
import { useEnableLtlRegistration } from '../../../../feature-flags/hooks/use-enable-ltl-registration';
import { useEnableLtlShipToAddressValidation } from '../../../../feature-flags/hooks/use-enable-ltl-ship-to-address-validation';
import { useCancelFreightFulfillmentPlan } from '../../../../hooks/freight/use-cancel-freight-fulfillment-plan';
import { useFreightAccountActivationFlow } from '../../../../hooks/freight/use-freight-account-activation-flow';
import { useShipFromResolution } from '../../../../hooks/freight/use-ship-from-resolution';
import { useModal } from '../../../../hooks/use-modal';
import { useSellerSetting } from '../../../../hooks/use-seller-setting';
import { SellerSettings } from '../../../../enums/seller-settings/seller-settings';
import { SellerSettingsOptions } from '../../../../enums/seller-settings/seller-settings-options';
import { createLookupHash } from '../../../../services/hash-service';
import { PusherService } from '../../../../services/pusher-service';
import Global from '../../../../store/global-selectors';
import { entitiesLoad, entitiesBulkload } from '../../../../store/entities/entities.actions';
import { processEntities } from '../../../../store/entities/entities.bulkload';
import { getFreightGridRequest } from '../../../../store/ltl/ltl.actions';
import { EventSourceStrings } from '../../../../tracking/tracking-constants';
import { useTrackOpenFreightQuote } from '../../../../tracking/use-track-open-freight-quote';
import { useTrackBookedFreightShipmentClick } from '../../../../tracking/use-track-booked-freight-shipment-click';
import { ShipFromModal } from '../../../../ship-from/modals';
import { useGlobalModalLaunchContext } from '../../../../contexts/modal-context/global-modal-context';
import { FreightInventoryWarningModal } from './components/freight-inventory-warning-modal/freight-inventory-warning-modal';
import { FreightNavigationButtons } from '../freight-navigation-buttons/freight-navigation-buttons';
import { freightBookingWizardContainerMessages } from './messages';

import { getMajorUnitType } from '@shipstation/client-common/utils/weight-utils';
import type { BaseWeightUnit } from '@shipstation/client-common/types/weight';
import type { DimensionUnit } from '@shipstation/client-common/types/dimensions';
import type {
  FreightBookingFormState,
  FreightDensityAction,
  WizardStepId,
} from '@shipstation/client-common/components/freight-booking-wizard';
import type { FreightFulfillmentPlanSaveQuoteRequest } from '@shipstation/client-common/types/freight-requests';
import type { AsyncTaskErrorEnvelope } from '@shipstation/client-common/types/common';
import type { BulkLoadResponse } from '@shipstation/client-common/types/bulkload-response';
import type { FreightCustomAttributeAvailable, FreightShipment } from '@shipstation/client-common/types/freight-responses';
import type { FulfillmentPlan } from '@shipstation/client-common/types/fulfillment-plan';
import type { LtlAddress, LtlQuote } from '@shipstation/client-common/types/ltl';
import type { AxiosError } from 'axios';
import type { LtlDefaults } from '@shipstation/client-common/types/ship-from';
import type { Customer, CustomerLtlDefaults } from '@shipstation/client-common/types/customer';
import {
  FreightQuoteTopicStatus,
  FreightQuoteErrorReason,
} from '../../../../orders/order-details/fulfillment-plan/sections/rate-card/actionable/freight-quote-topic';
import type { FreightQuoteTopic } from '../../../../orders/order-details/fulfillment-plan/sections/rate-card/actionable/freight-quote-topic';

// Wires NEW, load-saved-quote, and view-booked modes. After a booking is confirmed
// (or on view-booked mount) the container opens the global BookingConfirmationView modal.

/**
 * Shape of the object returned by ShipFromModal's onSuccess callback.
 * The underlying API (PUT /api/seller/shipfrom) returns the server-side ShipFrom contract,
 * where shipFromId is serialized as a JSON number. String() coercion in handlers is therefore
 * intentional: the store and form state always carry the id as a string for consistency.
 */
type ShipFromModalResult = {
  shipFromId?: number | string;
  ltlDefaults?: LtlDefaults | null;
};

const DEFAULT_ACTIVE_STEPS: readonly WizardStepId[] = ['ship-from'];

export const BOOKING_OPERATION = { NEW: 'NEW', UPDATE: 'UPDATE' } as const;

/**
 * The id the wizard opened with (or created) is the only plan it may edit. A bulkload
 * response after a split also carries sibling plans, so `[0]` is only trustworthy when
 * the wizard had no plan yet and the save just created one.
 */
const resolveFulfillmentPlanIdFromResponse = (result: BulkLoadResponse, currentFulfillmentPlanId: string | null): string | null =>
  currentFulfillmentPlanId ?? result?.fulfillmentPlans?.[0]?.fulfillmentPlanId ?? null;

const NO_CUSTOM_ATTRIBUTES: FreightCustomAttributeAvailable[] = [];

type FreightBookingWizardContainerProps =
  | {
      mode?: typeof WIZARD_MODE.NEW;
      isOpen: boolean;
      onClose: () => void;
      seedFormState: FreightBookingFormState;
      seedRates?: LtlQuote[];
      seedSelectedRate?: LtlQuote | null;
      source?: LtlFreightBookingSource;
      workflowGuid?: string;
    }
  | {
      // Launches the wizard directly with an empty form, bypassing the
      // FreightBrowser browse/quote handoff. Renders the presentational wizard
      // in 'new' mode with a fresh empty form state.
      mode: typeof WIZARD_MODE.NEW_DIRECT;
      isOpen: boolean;
      onClose: () => void;
      source?: LtlFreightBookingSource;
      workflowGuid?: string;
    }
  | {
      mode: typeof WIZARD_MODE.LOAD_SAVED_QUOTE;
      isOpen: boolean;
      onClose: () => void;
      fulfillmentPlanId: string;
      seedFormState: FreightBookingFormState;
      seedRates?: LtlQuote[];
      seedSelectedRate?: LtlQuote | null;
      source?: LtlFreightBookingSource;
      workflowGuid?: string;
      onNavigatePrevious?: (() => void) | null;
      onNavigateNext?: (() => void) | null;
    }
  | {
      mode: typeof WIZARD_MODE.VIEW_BOOKED;
      isOpen: boolean;
      onClose: () => void;
      fulfillmentPlanId: string;
      /** Pins which of the plan's shipments to open. Falls back to the plan's current shipment when omitted. */
      freightShipmentId?: string;
      source?: LtlFreightBookingSource;
      workflowGuid?: string;
      onNavigatePrevious?: (() => void) | null;
      onNavigateNext?: (() => void) | null;
    };

export const FreightBookingWizardContainer = (props: FreightBookingWizardContainerProps) => {
  const { isOpen, onClose } = props;
  const hasFreightBookRole = useUserHasFreightBookRole();
  const mode = props.mode ?? WIZARD_MODE.NEW;
  const source = 'source' in props ? props.source : undefined;
  const initialWorkflowGuid = 'workflowGuid' in props ? props.workflowGuid : undefined;
  const fulfillmentPlanId =
    props.mode === WIZARD_MODE.LOAD_SAVED_QUOTE || props.mode === WIZARD_MODE.VIEW_BOOKED ? props.fulfillmentPlanId : undefined;
  const onNavigatePrevious =
    props.mode === WIZARD_MODE.LOAD_SAVED_QUOTE || props.mode === WIZARD_MODE.VIEW_BOOKED ? (props.onNavigatePrevious ?? null) : null;
  const onNavigateNext =
    props.mode === WIZARD_MODE.LOAD_SAVED_QUOTE || props.mode === WIZARD_MODE.VIEW_BOOKED ? (props.onNavigateNext ?? null) : null;
  const requestedFreightShipmentId = props.mode === WIZARD_MODE.VIEW_BOOKED ? props.freightShipmentId : undefined;
  const intl = useIntl();
  const dispatch = useDispatch();
  const store = useStore();
  const isFreightNotificationsEnabled = useEnableLtlNotifications();
  const isCustomAttributesEnabled = useEnableFreightCustomAttributes();
  const isHazmatEnabled = useEnableLtlFreight();
  const isLtlRegistrationEnabled = useEnableLtlRegistration();
  const isShipToAddressValidationEnabled = useEnableLtlShipToAddressValidation();
  const defaultShipFromId = useSelector(Global.selectSellerDefaultShipFromId);
  const freightProviders = useSelector(Global.selectSellerFreightProviders);
  const sellerTimeZone: string = useSelector(Global.selectSellerTimeZoneIANA);
  const weightUnit = useSelector(Global.selectSellerDefaultWeightUnit) as BaseWeightUnit;
  const dimensionUnit = useSelector(Global.selectSellerDefaultDimensionUnit) as DimensionUnit;
  const currencyCode = (useSelector(Global.selectSellerDefaultCurrency) as string | undefined) ?? 'USD';
  const sellerId = useSelector(Global.selectSellerSellerId) as string | undefined;
  const states = useSelector(Global.selectReferenceDatagetStates);
  const displayWeightUnit = getMajorUnitType(weightUnit);

  const isQuotedStatus = useSelector((state) =>
    fulfillmentPlanId
      ? mapFreightFfpStatusId(
          (Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }) as FulfillmentPlan | undefined)?.freightConfiguration
            ?.freightFulfillmentPlanStatusID ?? 0,
        ) === FREIGHT_FFP_STATUS_QUOTED
      : false,
  );
  const isPendingQuote = mode === WIZARD_MODE.LOAD_SAVED_QUOTE && isQuotedStatus;
  const trackOpenFreightQuote = useTrackOpenFreightQuote();
  const trackBookedFreightShipmentClick = useTrackBookedFreightShipmentClick();

  const cancelFreightFulfillmentPlan = useCancelFreightFulfillmentPlan();

  const hasSeed = mode === WIZARD_MODE.NEW || mode === WIZARD_MODE.LOAD_SAVED_QUOTE;
  const [formState, setFormState] = useState<FreightBookingFormState>(() => {
    const today = DateTime.now().setZone(sellerTimeZone).toFormat('yyyy-MM-dd');
    if (hasSeed) {
      const seedFormState = (props as { seedFormState: FreightBookingFormState }).seedFormState;
      const isSeedShipDateInPast = seedFormState.shipmentDate
        ? DateTime.fromISO(seedFormState.shipmentDate).startOf('day') < DateTime.now().setZone(sellerTimeZone).startOf('day')
        : false;
      return {
        ...seedFormState,
        shipmentDate: !seedFormState.shipmentDate || isSeedShipDateInPast ? today : seedFormState.shipmentDate,
        selectedShipFromId: seedFormState.selectedShipFromId || (defaultShipFromId != null ? String(defaultShipFromId) : ''),
      };
    }
    return {
      ...createEmptyFormState(freightProviders?.[0]?.sellerFreightProviderId, displayWeightUnit, dimensionUnit),
      selectedShipFromId: defaultShipFromId != null ? String(defaultShipFromId) : '',
      shipmentDate: today,
    };
  });
  const [rates, setRates] = useState<LtlQuote[]>(() => (hasSeed ? ((props as { seedRates?: LtlQuote[] }).seedRates ?? []) : []));
  const [isLoadingRates, setIsLoadingRates] = useState(false);
  const [rateError, setRateError] = useState<string | undefined>(undefined);
  const [rateErrorCode, setRateErrorCode] = useState<string | null | undefined>(undefined);
  const [selectedRate, setSelectedRate] = useState<LtlQuote | null>(() =>
    hasSeed ? ((props as { seedSelectedRate?: LtlQuote | null }).seedSelectedRate ?? null) : null,
  );
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isDeletingDraft, setIsDeletingDraft] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  const { open: openAddShipFrom, close: closeAddShipFrom, isOpen: isAddShipFromOpen } = useModal();
  const { open: openEditShipFrom, close: closeEditShipFrom, isOpen: isEditShipFromOpen } = useModal();
  const { open: openInventoryWarning, close: closeInventoryWarning, isOpen: isInventoryWarningOpen } = useModal();
  const [inventoryWarningMessage, setInventoryWarningMessage] = useState('');
  const shipNoStockSetting = useSellerSetting(SellerSettings.InventoryShipNoStock);
  const { openBookingConfirmation, closeBookingConfirmation } = useGlobalModalLaunchContext() ?? {};
  // Fallback while getConnectedProviderId() waits on the post-registration Redux refresh to land.
  const justRegisteredProviderIdRef = useRef<number | null>(null);
  const zIndex = useMaxZ();

  const formStateRef = useRef(formState);
  formStateRef.current = formState;
  const selectedRateRef = useRef(selectedRate);
  selectedRateRef.current = selectedRate;
  const ratesRef = useRef(rates);
  ratesRef.current = rates;
  const sellerIdRef = useRef(sellerId);
  sellerIdRef.current = sellerId;
  // Snapshot of the selection taken right before a re-rate, used to reconcile once
  // the fresh rates have all arrived.
  const priorSelectedRateRef = useRef<LtlQuote | null>(null);
  const createdCustomerIdRef = useRef<string | null>(null);
  const createCustomerPromiseRef = useRef<Promise<string | null> | null>(null);

  // Tracks the fulfillmentPlanId for the in-progress save, so auto-save updates the
  // correct entity on subsequent calls.
  const fulfillmentPlanIdRef = useRef<string | null>(fulfillmentPlanId ?? null);

  // Guards against setState after the wizard closes/unmounts mid-fetch (load-saved-quote).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const sourceRef = useRef(source);
  // Set to true after any terminal action (save, book, cancel) so onClose doesn't
  // also fire closed_without_booking.
  const hasFiredTerminalEventRef = useRef(false);
  // Created on each wizard open; threaded into all subsequent freight tracking events
  // to correlate the full booking session.
  const workflowGuidRef = useRef<string>('');

  const [availableCustomAttributes, setAvailableCustomAttributes] = useState<FreightCustomAttributeAvailable[]>(NO_CUSTOM_ATTRIBUTES);
  const [isLoadingCustomAttributes, setIsLoadingCustomAttributes] = useState(false);
  useEffect(() => {
    setAvailableCustomAttributes(NO_CUSTOM_ATTRIBUTES);
    if (!isOpen || !isCustomAttributesEnabled || mode === WIZARD_MODE.VIEW_BOOKED) {
      setIsLoadingCustomAttributes(false);
      return;
    }
    setIsLoadingCustomAttributes(true);
    getAvailableFreightCustomAttributes()
      .then((attributes) => {
        if (mountedRef.current) setAvailableCustomAttributes(attributes);
      })
      .catch(() => {
        if (!mountedRef.current) return;
        NotificationService.showNotification({
          title: intl.formatMessage(freightBookingWizardContainerMessages.loadCustomAttributesFailed),
          duration: NOTIFICATION_DURATIONS.LONG,
          notificationType: NOTIFICATION_TYPE_ERROR,
        });
      })
      .finally(() => {
        if (mountedRef.current) setIsLoadingCustomAttributes(false);
      });
  }, [isOpen, isCustomAttributesEnabled, mode, intl]);

  useEffect(() => {
    if (!isOpen || mode === WIZARD_MODE.VIEW_BOOKED) return;
    workflowGuidRef.current = initialWorkflowGuid ?? GuidService.newGuid();
    Segment.trackEvent(LtlFreightTrackingEvents.BOOKING_FORM_OPENED, {
      source: sourceRef.current,
      workflowGuid: workflowGuidRef.current,
      fulfillmentPlanId: fulfillmentPlanIdRef.current,
    });
  }, [isOpen, mode, initialWorkflowGuid]);

  // Writing to freightQuoteState keeps the still-mounted rate-card Book action in sync
  // with wizard saves without requiring a separate re-quote round-trip.
  // Harmless no-op for NEW/NEW_DIRECT: no fulfillmentPlanId means no subscribers.
  const [, setFreightQuoteTopic] = useSubscribableState(
    freightQuoteState(fulfillmentPlanId, { status: 'idle' as const, bookableRate: null }),
  ) as [unknown, (value: FreightQuoteTopic) => void];

  const publishCurrentRateToRateCard = useCallback(() => {
    if (selectedRateRef.current) {
      setFreightQuoteTopic({ status: FreightQuoteTopicStatus.Ready, bookableRate: selectedRateRef.current });
    } else {
      setFreightQuoteTopic({ status: FreightQuoteTopicStatus.Error, bookableRate: null, errorReason: FreightQuoteErrorReason.NoMatch });
    }
  }, [setFreightQuoteTopic]);

  // When a re-quote starts, clear the rate-card topic to Loading so it doesn't briefly
  // show a stale bookable rate between the autosave and the fresh quote arriving.
  const setIsLoadingRatesAndSync = useCallback(
    (action: SetStateAction<boolean>) => {
      setIsLoadingRates(action);
      if (action === true) {
        setFreightQuoteTopic({ status: FreightQuoteTopicStatus.Loading, bookableRate: null });
      }
    },
    [setFreightQuoteTopic],
  ) as Dispatch<SetStateAction<boolean>>;

  const getConnectedProviderId = useCallback((): number | undefined => {
    return freightProviders?.[0]?.sellerFreightProviderId ?? undefined;
  }, [freightProviders]);

  // Fire trackOpenFreightQuote when the wizard opens with saved rates already present.
  useEffect(() => {
    if (mode !== WIZARD_MODE.LOAD_SAVED_QUOTE) return;
    if (rates.length > 0) {
      trackOpenFreightQuote();
    }
    // Run once on mount; trackOpenFreightQuote is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetches the fully-booked shipment record and loads it into the entities store so
  // the confirmation view's selector can read it. Mirrors the old context's
  // loadBookedShipmentInternal. Reused by both the post-book flow and view-booked mount.
  const loadBookedShipment = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const data = (await getBookedShipment(id)) as FreightShipment & AsyncTaskErrorEnvelope;
        if (!mountedRef.current) return false;
        if (data.success === false) {
          NotificationService.showGenericFailureNotification(
            undefined,
            data.errors?.[0]?.message ?? intl.formatMessage(freightBookingWizardContainerMessages.loadBookedShipmentFailed),
          );
          return false;
        }
        dispatch(entitiesLoad({ entities: { freightShipments: { [String(data.freightShipmentId)]: data } } }));
        return true;
      } catch (err) {
        if (!mountedRef.current) return false;
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          intl.formatMessage(freightBookingWizardContainerMessages.loadBookedShipmentFailed);
        NotificationService.showGenericFailureNotification(undefined, message);
        return false;
      }
    },
    [dispatch, intl],
  );

  // In view-booked mode, open the shipment the caller asked for; when it did not name one, fall back to the
  // FulfillmentPlan back-ref. Then render the confirmation view and fetch the full booked record.
  useEffect(() => {
    if (!isOpen || mode !== WIZARD_MODE.VIEW_BOOKED || !fulfillmentPlanId) return;

    const state = store.getState();
    const shipment = Global.selectEntitiesFreightShipmentByFulfillmentPlanId(state, { fulfillmentPlanId }) as FreightShipment | undefined;
    const planShipmentId = shipment?.freightShipmentId != null ? String(shipment.freightShipmentId) : null;
    const resolvedShipmentId = requestedFreightShipmentId ?? planShipmentId;

    if (resolvedShipmentId) {
      openBookingConfirmation?.({
        freightShipmentId: resolvedShipmentId,
        bookingConfirmation: null,
        source: sourceRef.current,
        isLoading: true,
      });
      void loadBookedShipment(resolvedShipmentId).then((loaded) => {
        if (!loaded) {
          closeBookingConfirmation?.();
        } else {
          openBookingConfirmation?.({
            freightShipmentId: resolvedShipmentId,
            bookingConfirmation: null,
            source: sourceRef.current,
            onNavigatePrevious,
            onNavigateNext,
            isLoading: false,
          });
        }
        onClose();
      });
    } else {
      NotificationService.showGenericFailureNotification(
        undefined,
        intl.formatMessage(freightBookingWizardContainerMessages.viewBookedShipmentDataMissing),
      );
      onClose();
    }
    // fulfillmentPlanId + requestedFreightShipmentId + isOpen drive the hydration; the setters/loader are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fulfillmentPlanId, requestedFreightShipmentId, isOpen, mode]);

  useEffect(() => {
    if (!rateError) return;
    Segment.trackEvent(LtlFreightTrackingEvents.RATE_ERROR_SHOWN, {
      error_message: rateError,
      workflowGuid: workflowGuidRef.current,
    });
  }, [rateError]);

  useEffect(() => {
    if (!isOpen || mode === WIZARD_MODE.VIEW_BOOKED) return;
    const handleTabClose = () => {
      if (hasFiredTerminalEventRef.current) return;
      Segment.trackEvent(LtlFreightTrackingEvents.BOOKING_CLOSE_WITHOUT_BOOKING, {
        source: sourceRef.current,
        operation: fulfillmentPlanIdRef.current ? BOOKING_OPERATION.UPDATE : BOOKING_OPERATION.NEW,
        ...(fulfillmentPlanIdRef.current ? { fulfillment_plan_id: fulfillmentPlanIdRef.current } : {}),
        workflowGuid: workflowGuidRef.current,
      });
    };
    window.addEventListener('beforeunload', handleTabClose);
    return () => {
      window.removeEventListener('beforeunload', handleTabClose);
    };
  }, [isOpen, mode]);

  const pendingQuoteGuidsRef = useRef<Set<string>>(new Set());
  const pendingBookGuidsRef = useRef<Set<string>>(new Set());

  const quotePusherService = useMemo(
    () => makeLtlPusherService(pendingQuoteGuidsRef, PusherService.bindToEvent.bind(PusherService), jobGuidManagerFactory),
    [],
  );
  const fulfillmentPlanQuotePusherService = useMemo(
    () => makeLtlPusherService(pendingQuoteGuidsRef, PusherService.bindToEvent.bind(PusherService), jobGuidManagerFactory),
    [],
  );
  const bookPusherService = useMemo(
    () => makeLtlPusherService(pendingBookGuidsRef, PusherService.bindToEvent.bind(PusherService), jobGuidManagerFactory),
    [],
  );

  const quoteProgressCallbacks = {
    onQuotes: (quotes: LtlQuote[]) => {
      // Append to the ref synchronously so onComplete (which can fire in the same
      // tick as the final onQuotes, before a re-render refreshes ratesRef) reconciles
      // against the freshly-arrived rates instead of a stale empty array.
      const next = [...ratesRef.current, ...quotes];
      ratesRef.current = next;
      setRates(next);
    },
    onError: (message: string, code?: string | null) => {
      setRateError(message);
      setRateErrorCode(code ?? null);
      setIsLoadingRates(false);
    },
    onComplete: () => {
      setIsLoadingRates(false);
      const reconciled = reconcileSelection(priorSelectedRateRef.current, ratesRef.current, formStateRef.current.insurance.isInsured);
      setSelectedRate(reconciled);
      if (ratesRef.current.length > 0) {
        Segment.trackEvent(LtlFreightTrackingEvents.RATES_RECEIVED, {
          source: sourceRef.current,
          workflowGuid: workflowGuidRef.current,
          quote_count: ratesRef.current.length,
        });
      }
    },
  };

  const { startListening: startQuoteListening, cancelListening: cancelQuoteListening } = useFreightQuoteProgress({
    pusherService: quotePusherService,
    pendingGuidsRef: pendingQuoteGuidsRef,
    ...quoteProgressCallbacks,
    timeoutMs: LTL_PUSHER_TIMEOUT_MS,
  });

  const { startListening: startFulfillmentPlanQuoteListening, cancelListening: cancelFulfillmentPlanQuoteListening } =
    useFreightFulfillmentPlanQuoteProgress({
      pusherService: fulfillmentPlanQuotePusherService,
      pendingGuidsRef: pendingQuoteGuidsRef,
      ...quoteProgressCallbacks,
      timeoutMs: LTL_PUSHER_TIMEOUT_MS,
    });

  const { startListening: startBookListening, cancelListening: cancelBookListening } = useFreightBookProgress({
    pusherService: bookPusherService,
    pendingGuidsRef: pendingBookGuidsRef,
    onConfirmation: (confirmation) => {
      hasFiredTerminalEventRef.current = true;
      const isNewBooking = !fulfillmentPlanIdRef.current;
      Segment.trackEvent(LtlFreightTrackingEvents.BOOKING_BOOK, {
        source: sourceRef.current,
        operation: isNewBooking ? BOOKING_OPERATION.NEW : BOOKING_OPERATION.UPDATE,
        ...(fulfillmentPlanIdRef.current ? { fulfillment_plan_id: fulfillmentPlanIdRef.current } : {}),
        is_saving_new_customer: formStateRef.current.shouldSaveAsNewCustomer ?? false,
        workflowGuid: workflowGuidRef.current,
      });
      const firstBookingKey = `ltl_first_freight_booking_${sellerIdRef.current}`;
      const hasBookedBefore = !!localStorage.getItem(firstBookingKey);
      // Not `isNewBooking && !hasBookedBefore`: hasBookedBefore already means "this seller has
      // never completed a freight booking before" regardless of whether this particular booking
      // created a fresh plan or finished one saved earlier (e.g. via Save Draft) — isNewBooking
      // would wrongly exclude that legitimate first-booking case.
      const isFirstFreightBooking = !hasBookedBefore;
      // Set after any booking (new or saved-quote update) so the flag survives across entry points
      if (!hasBookedBefore) {
        localStorage.setItem(firstBookingKey, '1');
      }
      dispatch(getFreightGridRequest());
      if (confirmation.freightShipmentId) {
        const id = String(confirmation.freightShipmentId);
        // Defer setIsBooking(false) until the fetch settles — prevents a double-click during the async gap.
        void loadBookedShipment(id).then((loaded) => {
          setIsBooking(false);
          if (!mountedRef.current) return;
          if (loaded) {
            openBookingConfirmation?.({
              freightShipmentId: id,
              bookingConfirmation: confirmation,
              source: sourceRef.current,
              isFirstFreightBooking,
            });
          }
          onClose();
        });
      } else {
        setIsBooking(false);
      }
    },
    onError: (message, code) => {
      // Insufficient inventory in warn mode: let the seller confirm and re-book with an
      // override instead of surfacing a dead-end error. ('allow' never fails here; 'error'
      // blocks server-side, so fall through to the generic error notification below.)
      if (code === FREIGHT_INSUFFICIENT_INVENTORY_CODE && shipNoStockSetting === SellerSettingsOptions.warnShip.value) {
        setInventoryWarningMessage(message);
        openInventoryWarning();
        setIsBooking(false);
        return;
      }
      NotificationService.showNotification({
        title: intl.formatMessage(freightBookingWizardContainerMessages.bookFailed),
        content: message,
        duration: NOTIFICATION_DURATIONS.LONG,
        notificationType: NOTIFICATION_TYPE_ERROR,
      });
      setIsBooking(false);
    },
    // eslint-disable-next-line no-empty-function
    onComplete: () => {},
    timeoutMs: LTL_BOOK_PUSHER_TIMEOUT_MS,
  });

  const { shipFromOptions } = useShipFromResolution(formState.selectedShipFromId);

  const buildSaveQuotePayload = useCallback((): FreightFulfillmentPlanSaveQuoteRequest => {
    const sellerFreightProviderId = getConnectedProviderId() ?? justRegisteredProviderIdRef.current ?? undefined;
    return buildSaveQuoteToFulfillmentPlanPayload(
      fulfillmentPlanIdRef.current,
      formStateRef.current,
      ratesRef.current,
      selectedRateRef.current,
      mapWeightUnit(weightUnit),
      mapDimensionUnit(dimensionUnit),
      sellerFreightProviderId,
    );
  }, [dimensionUnit, getConnectedProviderId, weightUnit]);

  const onQuoteSuccess = useCallback(
    (envelope: AsyncTaskErrorEnvelope, jobGuids: string[]) => {
      if (!mountedRef.current) return;
      if (envelope.success === false) {
        setRateError(envelope.errors?.[0]?.message ?? intl.formatMessage(freightBookingWizardContainerMessages.failedToRetrieveQuotes));
        setIsLoadingRates(false);
      }
      if (jobGuids.length === 0) {
        setIsLoadingRates(false);
      }
    },
    [intl],
  );

  const onQuoteError = useCallback(
    (err: AxiosError) => {
      if (!mountedRef.current) return;
      const responseData = (err as { response?: { data?: unknown } })?.response?.data;
      const message =
        (typeof responseData === 'string' ? responseData : null) ??
        (responseData as { Message?: string })?.Message ??
        (err instanceof Error ? err.message : null) ??
        intl.formatMessage(freightBookingWizardContainerMessages.rateFetchError);
      setRateError(message);
      setIsLoadingRates(false);
    },
    [intl],
  );

  const getFulfillmentPlan = useCallback(() => {
    if (!fulfillmentPlanIdRef.current) {
      return;
    }
    const fp = Global.selectEntitiesFulfillmentPlanById(store.getState(), { fulfillmentPlanId: fulfillmentPlanIdRef.current }) as
      | FulfillmentPlan
      | undefined;

    return fp;
  }, [store]);

  // The wizard's plan id never changes once set — a save response only fills it in when
  // the wizard had none and the save just created one.
  const adoptFulfillmentPlanIdFromResponse = useCallback(
    (result: BulkLoadResponse) => {
      const nextFulfillmentPlanId = resolveFulfillmentPlanIdFromResponse(result, fulfillmentPlanIdRef.current);
      if (nextFulfillmentPlanId) {
        fulfillmentPlanIdRef.current = nextFulfillmentPlanId;
      }
    },
    [fulfillmentPlanIdRef],
  );

  const onSaveConfigurationSuccess = useCallback(
    (result: BulkLoadResponse) => {
      if (!mountedRef.current) return;
      if (result?.fulfillmentPlans?.length) {
        adoptFulfillmentPlanIdFromResponse(result);
        dispatch(
          entitiesBulkload({
            ...processEntities(result),
            freightShipments: createLookupHash(result.freightShipments, 'freightShipmentId'),
          }),
        );
      }

      // Keep the rate-card Book action in sync with wizard autosave updates.
      publishCurrentRateToRateCard();
    },
    [adoptFulfillmentPlanIdFromResponse, dispatch, publishCurrentRateToRateCard],
  );

  const onQuoteFulfillmentPlanSuccess = useCallback(
    (envelope: AsyncTaskErrorEnvelope, jobGuids: string[]) => {
      if (!mountedRef.current) return;
      if (envelope.success === false) {
        setRateError(envelope.errors?.[0]?.message ?? intl.formatMessage(freightBookingWizardContainerMessages.failedToRetrieveQuotes));
        setIsLoadingRates(false);
        return;
      }
      if (jobGuids.length === 0) {
        setIsLoadingRates(false);
      }
    },
    [intl],
  );

  const onQuoteFulfillmentPlanError = useCallback(
    (err: AxiosError) => {
      if (!mountedRef.current) return;
      const responseData = (err as { response?: { data?: unknown } })?.response?.data;
      const message =
        (typeof responseData === 'string' ? responseData : null) ??
        (responseData as { Message?: string })?.Message ??
        (err instanceof Error ? err.message : null) ??
        intl.formatMessage(freightBookingWizardContainerMessages.rateFetchError);
      setRateError(message);
      setIsLoadingRates(false);
    },
    [intl],
  );

  const { onChange: handleChange, triggerRequote } = useHandleBookingFormChange({
    mode,
    isLtlRegistrationEnabled,
    fulfillmentPlanIdRef,
    getConnectedProviderId,
    setIsLoadingRates: setIsLoadingRatesAndSync,
    setRateError,
    setRateErrorCode,
    setRates,
    ratesRef,
    priorSelectedRateRef,
    selectedRateRef,
    cancelQuoteListening,
    mountedRef,
    startQuoteListening,
    getFulfillmentPlan,
    weightUnit,
    dimensionUnit,
    formStateRef,
    onSaveConfigurationSuccess,
    cancelFulfillmentPlanQuoteListening,
    startFulfillmentPlanQuoteListening,
    onQuoteSuccess,
    onQuoteError,
    onQuoteFulfillmentPlanSuccess,
    onQuoteFulfillmentPlanError,
  });

  const handleFormChange = useCallback(
    (next: FreightBookingFormState) => {
      // Keep the ref current synchronously — React defers the actual re-render, so without
      // this a second onFormChange fired later in the same event-handler tick (e.g.
      // AddressForm's onChange followed immediately by onCustomerSelect) would read
      // formStateRef.current as the stale pre-update snapshot and clobber this update.
      formStateRef.current = next;
      setFormState(next);
      if (rates.length > 0 && !getIsReadyToQuote(next)) {
        setRates([]);
        ratesRef.current = [];
        setSelectedRate(null);
      }
      handleChange(next);
    },
    [formStateRef, handleChange, rates.length, ratesRef],
  );

  // Called by ShipToStep after the user validates or applies a suggested address. For
  // existing orders (fulfillmentPlanId already set) we immediately persist the result so
  // the order entity stays in sync without waiting for a full draft save.
  const handleAddressValidated = useCallback(
    (addr: LtlAddress) => {
      const fpId = fulfillmentPlanIdRef.current;
      if (!fpId) return;
      saveShipToAndRecipient({
        fulfillmentPlanIds: [fpId],
        validateAddress: false,
        recipientInfo: { name: addr.name ?? null, phone: addr.phone ?? null, email: addr.email ?? null },
        shipTo: {
          name: addr.name ?? '',
          company: addr.company ?? null,
          phone: addr.phone ?? '',
          email: addr.email,
          line1: addr.line1 ?? '',
          line2: addr.line2 ?? '',
          line3: null,
          city: addr.city ?? '',
          state: addr.state ?? '',
          postalCode: addr.postalCode ?? '',
          countryCode: addr.countryCode ?? 'US',
          verificationStatus: addr.verificationStatus ?? null,
          verificationMessage: addr.verificationMessage ?? null,
          verificationUtc: addr.verificationUtc ?? null,
        },
      }).catch(() => {
        NotificationService.showGenericFailureNotification(
          undefined,
          intl.formatMessage(freightBookingWizardContainerMessages.saveValidatedAddressFailed),
        );
      });
    },
    [intl],
  );
  const isDirtyRef = useRef(false);

  const trackFreightBookingWizardChange = useTrackFreightBookingWizardChange({
    formStateRef,
    fulfillmentPlanIdRef,
    sourceRef,
    workflowGuidRef,
  });

  const handleDirtyingFormChange = useCallback(
    (next: FreightBookingFormState) => {
      isDirtyRef.current = true;
      // Diff against formStateRef before handleFormChange overwrites it with `next`.
      trackFreightBookingWizardChange(next);
      handleFormChange(next);
    },
    [handleFormChange, trackFreightBookingWizardChange],
  );

  // handleShipFromChange never fires on open, so LTL defaults for a pre-selected location
  // are never applied. This one-shot effect fills the gap. Skipped in VIEW_BOOKED only —
  // any mode that opens with a seed (NEW from the browse-rates/rate-browser handoff, and
  // LOAD_SAVED_QUOTE) uses the gap-fill variant, since the seed's pickupServices already
  // carries values the seller explicitly set (e.g. isLimitedAccess from the freight browser)
  // that a hard overwrite would silently clobber whenever the ship-from has saved defaults.
  // Only the truly-empty NEW_DIRECT open hard-applies defaults (SPD-28156).
  const hasHydratedInitialDefaults = useRef(false);
  useEffect(() => {
    if (hasHydratedInitialDefaults.current) return;
    if (mode === WIZARD_MODE.VIEW_BOOKED) return;
    if (!shipFromOptions.length) return;

    const current = formStateRef.current;
    if (!current.selectedShipFromId) return;

    const option = shipFromOptions.find((o) => o.id === current.selectedShipFromId);
    if (!option?.ltlDefaults) return;

    hasHydratedInitialDefaults.current = true;
    const hydratedState = hasSeed
      ? hydrateGapFillLtlDefaultsIntoFormState(current, option.ltlDefaults)
      : hydrateLtlDefaultsIntoFormState(current, option.ltlDefaults, { shouldHonorWillArrangePickupSeparately: false });
    handleFormChange(hydratedState);
  }, [shipFromOptions, mode, hasSeed, handleFormChange]);

  // triggerRequote takes an optional override provider id — wrapped so the refresh
  // button's click event never gets passed through as that argument.
  const handleRefresh = useCallback(() => {
    triggerRequote();
  }, [triggerRequote]);

  const handleAddShipFromSuccess = useCallback(
    (result: object) => {
      const newShipFrom = result as { shipFromId: string };
      closeAddShipFrom();
      // ShipFromContextProvider dispatches addNewShipFrom(result) before calling onSuccess,
      // so sellerData.shipFroms already includes the new entry — no fetchShipFroms needed.
      // Pickup fields are reset rather than inherited: a brand-new location has no saved
      // defaults, so carrying over the previous location's times/services would mislead
      // the user into thinking they are already configured for the new location.
      handleDirtyingFormChange({
        ...formStateRef.current,
        selectedShipFromId: newShipFrom.shipFromId,
        pickupReadyTime: '',
        pickupCloseTime: '',
        willArrangePickupSeparately: false,
        pickupServices: {
          isInsidePickup: false,
          isConstructionSitePickup: false,
          isLiftgatePickup: false,
          isNotifyBeforePickup: false,
          isResidentialPickup: false,
          isDropAtTerminal: false,
          isGroceryConsolidationPickup: false,
          isLimitedAccess: false,
          isTradeshowPickup: false,
        },
        pickupInstructions: '',
        shouldSaveLtlDefaults: false,
      });
      Segment.trackEvent(LtlFreightTrackingEvents.ADD_SHIP_FROM, {
        workflowGuid: workflowGuidRef.current,
      });
      NotificationService.showGenericSuccessNotification(
        undefined,
        intl.formatMessage(freightBookingWizardContainerMessages.addShipFromSuccess),
      );
    },
    [closeAddShipFrom, handleDirtyingFormChange, intl],
  );

  const handleEditShipFromSuccess = useCallback(
    (result: ShipFromModalResult) => {
      closeEditShipFrom();
      dispatch(editShipFrom(result));
      if (result.shipFromId && String(result.shipFromId) === formStateRef.current.selectedShipFromId) {
        const newLtlDefaults = result.ltlDefaults ?? null;
        handleDirtyingFormChange(hydrateLtlDefaultsIntoFormState(formStateRef.current, newLtlDefaults));
      }
      NotificationService.showGenericSuccessNotification(
        undefined,
        intl.formatMessage(freightBookingWizardContainerMessages.editShipFromSuccess),
      );
    },
    [closeEditShipFrom, dispatch, formStateRef, handleDirtyingFormChange, intl],
  );

  const [customerLtlDefaults, setCustomerLtlDefaults] = useState<CustomerLtlDefaults | null>(null);
  const [isLoadingCustomerLtlDefaults, setIsLoadingCustomerLtlDefaults] = useState(false);

  const handleCustomerSelect = useCallback(
    (customer: Customer) => {
      Segment.trackEvent(LtlFreightTrackingEvents.EXISTING_CUSTOMER_USED, {
        workflowGuid: workflowGuidRef.current,
      });

      const customerId = customer.customerId;
      // Set the id immediately (and clear any previously loaded defaults) so it's
      // tracked even if the fetch below is slow, fails, or the destination has no
      // matching customer at all (AC7 — no error, no blocked booking).
      setCustomerLtlDefaults(null);
      handleDirtyingFormChange(applyCustomerLtlDefaultsToFormState(formStateRef.current, customerId, null));

      if (!customerId) return;

      setIsLoadingCustomerLtlDefaults(true);
      getCustomerLtlDefaults(customerId)
        .then((defaults) => {
          // Guard against unmount and stale responses (different customer selected before fetch resolved).
          if (!mountedRef.current || formStateRef.current.selectedCustomerId !== customerId) return;
          setCustomerLtlDefaults(defaults);
          handleDirtyingFormChange(hydrateCustomerLtlDefaultsIntoFormState(formStateRef.current, defaults));
        })
        .catch(() => {
          // Swallow — a failed fetch just means no defaults were applied (AC5/AC7 behavior).
        })
        .finally(() => {
          if (mountedRef.current) setIsLoadingCustomerLtlDefaults(false);
        });
    },
    [formStateRef, handleDirtyingFormChange, mountedRef],
  );

  const canSaveLtlDeliveryDefaults = useUserHasConfigurationRole();

  const [isSavingLtlDeliveryDefaults, setIsSavingLtlDeliveryDefaults] = useState(false);

  const handleSaveLtlDeliveryDefaults = useCallback(
    async (customerIdOverride?: string | null) => {
      const state = formStateRef.current;
      const customerId = customerIdOverride ?? state.selectedCustomerId;
      if (!customerId) return;

      // Re-checked here rather than trusting the checkbox: the user can revert their
      // edits after ticking it, which disables the checkbox without clearing the flag.
      if (!areCustomerLtlDefaultsDirty(state, customerLtlDefaults)) return;

      const payload = buildCustomerLtlDefaultsFromFormState(state);

      setIsSavingLtlDeliveryDefaults(true);
      try {
        await saveCustomerLtlDefaults(customerId, payload);
        setCustomerLtlDefaults(payload);
        handleDirtyingFormChange({ ...formStateRef.current, shouldSaveLtlDeliveryDefaults: false });
        NotificationService.showGenericSuccessNotification(
          undefined,
          intl.formatMessage(freightBookingWizardContainerMessages.saveLtlDeliveryDefaultsSuccess),
        );
        Segment.trackEvent(LtlFreightTrackingEvents.SAVE_LTL_DELIVERY_DEFAULTS, {
          workflowGuid: workflowGuidRef.current,
        });
      } catch {
        NotificationService.showGenericFailureNotification(
          undefined,
          intl.formatMessage(freightBookingWizardContainerMessages.saveLtlDeliveryDefaultsFailed),
        );
      } finally {
        if (mountedRef.current) setIsSavingLtlDeliveryDefaults(false);
      }
    },
    [customerLtlDefaults, formStateRef, handleDirtyingFormChange, intl, mountedRef],
  );

  const handleDensityAction = useCallback((action: FreightDensityAction) => {
    Segment.trackEvent(LtlFreightTrackingEvents.CALC_DENSITY_CLICKED, {
      action,
      workflowGuid: workflowGuidRef.current,
    });
  }, []);

  const handleCommodityPresetSelect = useCallback((presetId: number) => {
    Segment.trackEvent(LtlFreightTrackingEvents.EXISTING_COMMODITY_PRESET_USED, {
      preset_id: presetId,
      workflowGuid: workflowGuidRef.current,
    });
  }, []);

  const canSaveLtlDefaults = useUserHasConfigurationRole();

  const [isSavingLtlDefaults, setIsSavingLtlDefaults] = useState(false);

  const selectedShipFromOption = shipFromOptions.find((o) => o.id === formState.selectedShipFromId);
  const hasSavedLtlDefaults = !!selectedShipFromOption?.ltlDefaults;

  const sellerShipFroms = useSelector(Global.selectSellerShipFroms);

  const handleSaveLtlDefaults = useCallback(async () => {
    const state = formStateRef.current;
    const shipFromId = state.selectedShipFromId;
    if (!shipFromId) return;
    if (!hasCompleteLtlPickupWindow(state)) return;

    const currentShipFrom = sellerShipFroms?.find((sf: { shipFromId: string | number }) => String(sf.shipFromId) === shipFromId);

    // Re-checked here rather than trusting the checkbox: the user can revert their
    // edits after ticking it, which disables the checkbox without clearing the flag.
    if (!areLtlDefaultsDirty(state, currentShipFrom?.ltlDefaults ?? null)) return;

    const payload = buildLtlDefaultsFromFormState(state, currentShipFrom?.ltlDefaults ?? null);

    setIsSavingLtlDefaults(true);
    try {
      await saveShipFromLtlDefaults(shipFromId, payload);
      // Local merge — no round-trip GET needed; editShipFrom replaces the full entry.
      if (currentShipFrom) {
        dispatch(editShipFrom({ ...currentShipFrom, ltlDefaults: payload }));
      }
      NotificationService.showGenericSuccessNotification(
        undefined,
        intl.formatMessage(freightBookingWizardContainerMessages.saveLtlDefaultsSuccess),
      );
      Segment.trackEvent(LtlFreightTrackingEvents.SAVE_LTL_DEFAULTS, {
        workflowGuid: workflowGuidRef.current,
      });
    } catch {
      NotificationService.showGenericFailureNotification(
        undefined,
        intl.formatMessage(freightBookingWizardContainerMessages.saveLtlDefaultsFailed),
      );
    } finally {
      if (mountedRef.current) setIsSavingLtlDefaults(false);
    }
  }, [dispatch, formStateRef, intl, mountedRef, sellerShipFroms]);

  const handlePasteAddressUsed = useCallback(() => {
    Segment.trackEvent(LtlFreightTrackingEvents.PASTE_ADDRESS_USED, {
      workflowGuid: workflowGuidRef.current,
    });
  }, []);

  const maybeCreateCustomer = useCallback(
    (state: FreightBookingFormState): Promise<string | null> => {
      if (!state.shouldSaveAsNewCustomer) return Promise.resolve(null);
      if (createCustomerPromiseRef.current) return createCustomerPromiseRef.current;

      const addr = state.destinationAddress;
      const promise = createCustomer({
        name: addr.name,
        email: addr.email,
        company: addr.company ?? '',
        street1: addr.line1,
        street2: addr.line2,
        city: addr.city,
        state: addr.state,
        postalCode: addr.postalCode,
        countryCode: addr.countryCode,
        phone: addr.phone,
      })
        .then((newId) => {
          createdCustomerIdRef.current = newId ?? null;
          NotificationService.showGenericSuccessNotification(
            undefined,
            intl.formatMessage(freightBookingWizardContainerMessages.createCustomerSuccess),
          );
          return createdCustomerIdRef.current;
        })
        .catch(() => {
          NotificationService.showGenericFailureNotification(
            undefined,
            intl.formatMessage(freightBookingWizardContainerMessages.createCustomerFailed),
          );
          return null;
        });

      createCustomerPromiseRef.current = promise;
      return promise;
    },
    [intl],
  );

  const handleSaveDraft = useCallback(async (): Promise<boolean> => {
    const payload = buildSaveQuotePayload();

    // Set before the first await: the defaults save is a network round-trip, and
    // leaving the footer button live through it lets a second click create a
    // duplicate fulfillment plan (fulfillmentPlanIdRef is still null).
    setIsSavingDraft(true);
    let newCustomerId = null;
    if (formStateRef.current.shouldSaveLtlDeliveryDefaults) {
      newCustomerId = await maybeCreateCustomer(formStateRef.current);
    } else {
      void maybeCreateCustomer(formStateRef.current);
    }
    if (formStateRef.current.shouldSaveLtlDefaults) {
      await handleSaveLtlDefaults();
    }
    if (formStateRef.current.shouldSaveLtlDeliveryDefaults) {
      await handleSaveLtlDeliveryDefaults(newCustomerId);
    }
    try {
      const operation = fulfillmentPlanIdRef.current ? BOOKING_OPERATION.UPDATE : BOOKING_OPERATION.NEW;
      const result = (await saveQuoteToFulfillmentPlan(payload)) as BulkLoadResponse;
      adoptFulfillmentPlanIdFromResponse(result);
      hasFiredTerminalEventRef.current = true;
      Segment.trackEvent(LtlFreightTrackingEvents.BOOKING_SAVE_DRAFT, {
        source: sourceRef.current,
        operation,
        ...(fulfillmentPlanIdRef.current ? { fulfillment_plan_id: fulfillmentPlanIdRef.current } : {}),
        workflowGuid: workflowGuidRef.current,
      });
      dispatch(getFreightGridRequest());
      // Publish to freightQuoteState so the rate-card and Book button update immediately without a re-quote.
      publishCurrentRateToRateCard();
      NotificationService.showGenericSuccessNotification(
        undefined,
        intl.formatMessage(freightBookingWizardContainerMessages.saveQuoteSuccess),
      );
      onClose();
      return true;
    } catch (err) {
      let errorMessage;
      if (typeof (err as { response?: { data?: string } })?.response?.data === 'string') {
        errorMessage = (err as { response?: { data?: string } })?.response?.data;
      }
      if (!errorMessage) {
        errorMessage =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          intl.formatMessage(freightBookingWizardContainerMessages.saveQuoteFailed);
      }
      NotificationService.showGenericFailureNotification(undefined, errorMessage);
      return false;
    } finally {
      setIsSavingDraft(false);
    }
  }, [
    adoptFulfillmentPlanIdFromResponse,
    buildSaveQuotePayload,
    dispatch,
    handleSaveLtlDefaults,
    handleSaveLtlDeliveryDefaults,
    intl,
    maybeCreateCustomer,
    onClose,
    publishCurrentRateToRateCard,
  ]);

  const handleDeleteDraft = useCallback(async () => {
    const id = fulfillmentPlanIdRef.current;
    if (!id) return;
    setIsDeletingDraft(true);
    const didSucceed = await cancelFreightFulfillmentPlan([id]);
    if (didSucceed) {
      hasFiredTerminalEventRef.current = true;
      Segment.trackEvent(LtlFreightTrackingEvents.BOOKING_DELETE, {
        source: sourceRef.current,
        fulfillment_plan_id: id,
        workflowGuid: workflowGuidRef.current,
      });
      dispatch(getFreightGridRequest());
      onClose();
    } else {
      NotificationService.showGenericFailureNotification(
        undefined,
        intl.formatMessage(freightBookingWizardContainerMessages.deleteDraftFailed),
      );
    }
    if (mountedRef.current) setIsDeletingDraft(false);
  }, [cancelFreightFulfillmentPlan, dispatch, intl, onClose]);

  const continueAfterProviderConnected = useCallback(
    async (sellerFreightProviderId: number) => {
      justRegisteredProviderIdRef.current = sellerFreightProviderId;
      const updatedFormState = { ...formStateRef.current, sellerFreightProviderID: sellerFreightProviderId };
      formStateRef.current = updatedFormState;
      isDirtyRef.current = true;
      setFormState(updatedFormState);

      if (fulfillmentPlanIdRef.current) {
        const fp = getFulfillmentPlan();
        const savePayload = buildSaveConfigurationPayload(
          fulfillmentPlanIdRef.current,
          formStateRef.current,
          null,
          ratesRef.current,
          mapWeightUnit(weightUnit),
          mapDimensionUnit(dimensionUnit),
          fp?.freightConfiguration?.freightFulfillmentPlanStatusID ?? null,
          fp?.freightConfiguration?.freightNumber ?? null,
        );
        if (savePayload) {
          try {
            const result = await saveConfiguration(fulfillmentPlanIdRef.current, savePayload);
            onSaveConfigurationSuccess(result);
          } catch (err) {
            const errorMessage =
              (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
              intl.formatMessage(freightBookingWizardContainerMessages.bookFailed);
            NotificationService.showGenericFailureNotification(undefined, errorMessage);
          }
        }
      }

      triggerRequote(sellerFreightProviderId);
    },
    [getFulfillmentPlan, weightUnit, dimensionUnit, intl, onSaveConfigurationSuccess, triggerRequote],
  );

  // WwexRegistrationContainer and ConnectFreightProviderModalContextProvider both resolve a
  // real provider id themselves (awaiting the fresh provider list when their own success
  // callback doesn't hand one back directly) before calling this — so a null id here means
  // registration genuinely didn't produce a connectable account, not "not resolved yet".
  const handleRegistrationSuccess = useCallback(
    (sellerFreightProviderId: number | null) => {
      setSelectedRate(null);

      if (!sellerFreightProviderId) {
        NotificationService.showGenericFailureNotification(
          undefined,
          intl.formatMessage(freightBookingWizardContainerMessages.noProviderConnected),
        );
        return;
      }

      void continueAfterProviderConnected(sellerFreightProviderId);
    },
    [continueAfterProviderConnected, intl],
  );

  const {
    openChooser: openFreightAccountChooser,
    freightAccountActivationModals,
    hasJustConnectedProvider,
  } = useFreightAccountActivationFlow(EventSourceStrings.FREIGHT_BOOKING_WIZARD, handleRegistrationSuccess, handleSaveDraft);

  const handleBook = useCallback(
    async (overrideInventoryWarning = false) => {
      // Booking requires the seller's own registered WWEX account; house-account rates are display-only.
      const requiresRegistrationBeforeBooking =
        isLtlRegistrationEnabled && !getConnectedProviderId() && !justRegisteredProviderIdRef.current && !hasJustConnectedProvider();
      if (requiresRegistrationBeforeBooking) {
        openFreightAccountChooser();
        return;
      }

      const selected = selectedRateRef.current;
      if (!selected || !isReadyToBook(formStateRef.current, { selectedRate: selected })) return;

      setIsBooking(true);
      cancelBookListening();
      let newCustomerId = null;
      if (formStateRef.current.shouldSaveLtlDeliveryDefaults) {
        newCustomerId = await maybeCreateCustomer(formStateRef.current);
      } else {
        void maybeCreateCustomer(formStateRef.current);
      }
      if (formStateRef.current.shouldSaveLtlDefaults) {
        await handleSaveLtlDefaults();
      }
      if (formStateRef.current.shouldSaveLtlDeliveryDefaults) {
        await handleSaveLtlDeliveryDefaults(newCustomerId);
      }

      const current = formStateRef.current;

      // If no fulfillment plan exists yet (e.g. the wizard was opened without auto-save
      // having fired), create one first so the book endpoint has a plan to act on.
      if (!fulfillmentPlanIdRef.current) {
        const savePayload = buildSaveQuotePayload();
        if (!savePayload) {
          setIsBooking(false);
          return;
        }
        try {
          const result = (await saveQuoteToFulfillmentPlan(savePayload)) as BulkLoadResponse;
          adoptFulfillmentPlanIdFromResponse(result);
          if (fulfillmentPlanIdRef.current) {
            dispatch(getFreightGridRequest());
          }
        } catch (err) {
          const errorMessage =
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            intl.formatMessage(freightBookingWizardContainerMessages.bookFailed);
          NotificationService.showGenericFailureNotification(undefined, errorMessage);
          setIsBooking(false);
          return;
        }
      } else {
        const fp = getFulfillmentPlan();
        const savePayload = buildSaveConfigurationPayload(
          fulfillmentPlanIdRef.current,
          formStateRef.current,
          selectedRateRef.current,
          ratesRef.current,
          mapWeightUnit(weightUnit),
          mapDimensionUnit(dimensionUnit),
          fp?.freightConfiguration?.freightFulfillmentPlanStatusID ?? null,
          fp?.freightConfiguration?.freightNumber ?? null,
        );
        if (!savePayload) {
          NotificationService.showGenericFailureNotification(
            undefined,
            intl.formatMessage(freightBookingWizardContainerMessages.bookFailed),
          );
          setIsBooking(false);
          return;
        }
        try {
          const result = await saveConfiguration(fulfillmentPlanIdRef.current, savePayload);
          onSaveConfigurationSuccess(result);
        } catch (err) {
          const errorMessage =
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            intl.formatMessage(freightBookingWizardContainerMessages.bookFailed);
          NotificationService.showGenericFailureNotification(undefined, errorMessage);
          setIsBooking(false);
          return;
        }
      }

      if (!fulfillmentPlanIdRef.current) {
        NotificationService.showGenericFailureNotification(
          undefined,
          intl.formatMessage(freightBookingWizardContainerMessages.bookSuccessButNoFulfillmentPlan),
        );
        setIsBooking(false);
        return;
      }

      const payload = buildBookFulfillmentPlanPayload(
        fulfillmentPlanIdRef.current,
        current,
        selected.productTransactionId,
        overrideInventoryWarning,
      );

      try {
        const data = (await bookFulfillmentPlan(payload)) as AsyncTaskErrorEnvelope;
        if (data.success === false) {
          NotificationService.showGenericFailureNotification(
            undefined,
            data.errors?.[0]?.message ?? intl.formatMessage(freightBookingWizardContainerMessages.bookFailed),
          );
          setIsBooking(false);
          return;
        }
        const jobGuids: string[] = (data.jobGuids ?? data.JobGuids ?? []) as string[];
        startBookListening(jobGuids);
        // The Pusher 'freight-book' event (useFreightBookProgress.onConfirmation) finishes the flow.
      } catch (err) {
        const responseData = (err as { response?: { data?: unknown } })?.response?.data;
        const errorMessage =
          typeof responseData === 'string'
            ? responseData
            : ((responseData as { message?: string })?.message ?? intl.formatMessage(freightBookingWizardContainerMessages.bookFailed));
        NotificationService.showGenericFailureNotification(undefined, errorMessage);
        setIsBooking(false);
      }
    },
    [
      adoptFulfillmentPlanIdFromResponse,
      isLtlRegistrationEnabled,
      getConnectedProviderId,
      hasJustConnectedProvider,
      openFreightAccountChooser,
      buildSaveQuotePayload,
      cancelBookListening,
      handleSaveLtlDefaults,
      handleSaveLtlDeliveryDefaults,
      intl,
      maybeCreateCustomer,
      startBookListening,
      dispatch,
      weightUnit,
      dimensionUnit,
      getFulfillmentPlan,
      onSaveConfigurationSuccess,
    ],
  );

  const { open: openConfirmDialog, close: closeConfirmDialog, isOpen: isConfirmCloseOpen } = useModal();

  const closeWizard = useCallback(() => {
    if (!hasFiredTerminalEventRef.current && mode !== WIZARD_MODE.VIEW_BOOKED) {
      Segment.trackEvent(LtlFreightTrackingEvents.BOOKING_CLOSE_WITHOUT_BOOKING, {
        source: sourceRef.current,
        operation: fulfillmentPlanIdRef.current ? BOOKING_OPERATION.UPDATE : BOOKING_OPERATION.NEW,
        ...(fulfillmentPlanIdRef.current ? { fulfillment_plan_id: fulfillmentPlanIdRef.current } : {}),
        workflowGuid: workflowGuidRef.current,
      });
      hasFiredTerminalEventRef.current = true;
    }
    onClose();
  }, [mode, onClose]);

  const handleClose = useCallback(() => {
    // Escape can reach both the dialog's hotkey and the Drawer's listener; ignore the second one.
    if (isConfirmCloseOpen) return;
    if (mode !== WIZARD_MODE.VIEW_BOOKED && isDirtyRef.current && !fulfillmentPlanIdRef.current) {
      openConfirmDialog();
      return;
    }
    closeWizard();
  }, [closeWizard, isConfirmCloseOpen, mode, openConfirmDialog]);

  // The wizard's book Button forwards its click event to onBook; wrap so the event
  // object never leaks in as a truthy `overrideInventoryWarning`.
  const handleBookInitial = useCallback(() => {
    trackBookedFreightShipmentClick('wizard');
    void handleBook(false);
  }, [handleBook, trackBookedFreightShipmentClick]);

  const handleBookWithOverride = useCallback(() => {
    closeInventoryWarning();
    void handleBook(true);
  }, [closeInventoryWarning, handleBook]);

  // For load-saved-quote, derive per-step state from the first invalid step at mount time:
  //   - Steps strictly before the first invalid one are pre-checked (green check, summary).
  //   - The first invalid step is active (expanded) and has its inline errors visible immediately.
  //   - Steps after the first invalid one are pending and silent, even if they are themselves valid.
  // Empty dep array: these are mount-time initializers — callers pass `initial*` props once and
  // the wizard owns the state from that point forward.
  const { initialCompletedSteps, initialActiveSteps } = useMemo(() => {
    // An unrated plan (no saved quotes) gets the default ship-from landing.
    const hydratedState = mode === WIZARD_MODE.LOAD_SAVED_QUOTE && rates.length > 0 ? formState : null;
    if (!hydratedState) {
      return { initialCompletedSteps: undefined, initialActiveSteps: DEFAULT_ACTIVE_STEPS as readonly WizardStepId[] };
    }
    const firstInvalidStep = getFirstInvalidStep(hydratedState);
    const completedStepsRecord = Object.fromEntries(getStepsBeforeFirstInvalid(hydratedState).map((step) => [step, true] as const));
    return {
      initialCompletedSteps: completedStepsRecord,
      // No gaps anywhere → land on the final step (as today for a complete plan).
      initialActiveSteps: [firstInvalidStep ?? WIZARD_STEP_IDS[WIZARD_STEP_IDS.length - 1]] as readonly WizardStepId[],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {mode !== WIZARD_MODE.VIEW_BOOKED && (
        <>
          <FreightBookingWizard
            isOpen={isOpen}
            mode={mode}
            onClose={handleClose}
            formState={formState}
            onFormChange={handleDirtyingFormChange}
            onAddressValidated={handleAddressValidated}
            onAddShipFrom={openAddShipFrom}
            onEditShipFrom={formState.selectedShipFromId ? openEditShipFrom : undefined}
            onCommodityPresetSelect={handleCommodityPresetSelect}
            onCustomerSelect={handleCustomerSelect}
            onPasteAddressUsed={handlePasteAddressUsed}
            onDensityAction={handleDensityAction}
            shipFromOptions={shipFromOptions}
            states={states}
            displayWeightUnit={displayWeightUnit}
            displayDimensionUnit={dimensionUnit}
            currencyCode={currencyCode}
            rates={rates}
            isLoadingRates={isLoadingRates}
            rateError={rateError}
            rateErrorCode={rateErrorCode}
            selectedRate={selectedRate}
            onSelectRate={setSelectedRate}
            onRefresh={handleRefresh}
            onSaveDraft={handleSaveDraft}
            onBook={handleBookInitial}
            isSavingDraft={isSavingDraft}
            isBooking={isBooking}
            onDeleteDraft={handleDeleteDraft}
            isDeletingDraft={isDeletingDraft}
            canDeleteDraft={isPendingQuote}
            canManageBookings={hasFreightBookRole}
            manageBookingsDisabledTooltip={
              hasFreightBookRole ? undefined : intl.formatMessage(freightActionButtonMessages.freightBookDisabledTooltip)
            }
            initialCompletedSteps={initialCompletedSteps}
            initialActiveSteps={initialActiveSteps}
            isFreightNotificationsEnabled={isFreightNotificationsEnabled}
            isCustomAttributesEnabled={isCustomAttributesEnabled}
            availableCustomAttributes={availableCustomAttributes}
            isLoadingCustomAttributes={isLoadingCustomAttributes}
            isHazmatEnabled={isHazmatEnabled}
            isShipToAddressValidationEnabled={isShipToAddressValidationEnabled}
            zIndex={zIndex}
            canSaveLtlDefaults={canSaveLtlDefaults}
            hasSavedLtlDefaults={hasSavedLtlDefaults}
            isSavingLtlDefaults={isSavingLtlDefaults}
            canSaveLtlDeliveryDefaults={canSaveLtlDeliveryDefaults}
            hasSavedLtlDeliveryDefaults={!!customerLtlDefaults}
            isSavingLtlDeliveryDefaults={isSavingLtlDeliveryDefaults}
            isLoadingCustomerLtlDefaults={isLoadingCustomerLtlDefaults}
            loadedCustomerLtlDefaults={customerLtlDefaults}
          />
          <FreightNavigationButtons onNavigatePrevious={onNavigatePrevious} onNavigateNext={onNavigateNext} />
        </>
      )}
      {isAddShipFromOpen && (
        <ShipFromModal onClose={closeAddShipFrom} onSuccess={handleAddShipFromSuccess} source={EventSourceStrings.LTL_FREIGHT_WIZARD} />
      )}
      {isEditShipFromOpen && formState.selectedShipFromId && (
        <ShipFromModal
          onClose={closeEditShipFrom}
          onSuccess={handleEditShipFromSuccess}
          shipFromId={formState.selectedShipFromId}
          source={EventSourceStrings.LTL_FREIGHT_WIZARD}
        />
      )}
      {isInventoryWarningOpen && (
        <FreightInventoryWarningModal
          message={inventoryWarningMessage}
          onContinue={handleBookWithOverride}
          onClose={closeInventoryWarning}
        />
      )}
      {freightAccountActivationModals}
      {mode !== WIZARD_MODE.VIEW_BOOKED && (
        <ConfirmCloseModal
          isOpen={isConfirmCloseOpen}
          isSavingDraft={isSavingDraft}
          zIndex={zIndex}
          onClose={closeConfirmDialog}
          onSaveDraft={hasFreightBookRole ? handleSaveDraft : undefined}
          onDiscard={closeWizard}
        />
      )}
    </>
  );
};
